/**
 * dealerEcountListMixin — 딜러/ECOUNT list 화면 공통 패턴
 *
 * SPEC: SPEC-ECOUNT-FE-POLISH-001 Phase B-1
 * SPEC: SPEC-CHINA-ECOUNT-DEALER-FILTER-URL-SYNC-001 (양방향 URL query 동기화 추가)
 *
 * 포함:
 * - isMobileView data + onResize listener (mounted/beforeUnmount)
 * - timezoneInfo computed (locale-aware: ko=KST UTC+9, zh/en=UTC+8)
 * - formatDateTime / formatDateOnly / formatNumber / formatDuration / truncate 메서드
 * - onSortChange / onPageChange / onPageSizeChange / onToolbarPageSizeChange 페이지네이션 핸들러
 * - serializeFiltersToQuery / deserializeQueryToFilters — URL ↔ filters 양방향 변환
 * - setupQueryFilterSync — mounted에서 1회 호출하여 filters deep watch 등록
 *
 * 사용:
 *   import dealerEcountListMixin from '@/mixins/dealerEcountListMixin';
 *   export default {
 *     mixins: [dealerEcountListMixin],
 *     data() { return { ...this.listMixinData(), filters: {...}, sortBy: '...', ... }; },
 *     mounted() { this.applyQueryToFilters(); this.setupQueryFilterSync(); this.fetchData(); },
 *     methods: { fetchData() {...} }
 *   };
 *
 * @MX:ANCHOR: [AUTO] 6~7 화면 공통 진입점. fan_in >= 6.
 * @MX:REASON: 코드 중복 제거 + 향후 룩앤필 일관성 유지 (한 곳 fix로 전체 적용).
 */
import dayjs from 'dayjs';

export default {
  data() {
    return {
      isMobileView: window.innerWidth <= 767,
    };
  },

  computed: {
    /**
     * locale 기반 timezone 정보.
     * ko = KST (UTC+9), zh/en/ja/vi = 중국 영업일 (UTC+8)
     */
    timezoneInfo() {
      const loc = this.$i18n?.locale || 'zh';
      if (loc === 'ko') return { offsetHours: 9, label: 'KST (UTC+9)' };
      // zh/en/ja/vi 모두 UTC+8
      return {
        offsetHours: 8,
        label: this.$te('dealerGsp_tz_label_cn') ? this.$t('dealerGsp_tz_label_cn') : 'UTC+8',
      };
    },
  },

  mounted() {
    window.addEventListener('resize', this.onResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    // setupQueryFilterSync가 등록한 watch 핸들러 정리
    if (this._queryFilterSyncUnwatch) {
      this._queryFilterSyncUnwatch();
      this._queryFilterSyncUnwatch = null;
    }
    if (this._queryFilterSyncTimer) {
      clearTimeout(this._queryFilterSyncTimer);
      this._queryFilterSyncTimer = null;
    }
  },

  methods: {
    onResize() {
      this.isMobileView = window.innerWidth <= 767;
    },

    /**
     * ISO 시각 → "YYYY-MM-DD HH:MM:SS" (locale 기반 TZ offset 적용)
     */
    formatDateTime(v) {
      if (!v) return '-';
      const d = new Date(v);
      if (isNaN(d.getTime())) return '-';
      const offsetMs = this.timezoneInfo.offsetHours * 60 * 60 * 1000;
      const local = new Date(d.getTime() + offsetMs);
      const iso = local.toISOString();
      return `${iso.slice(0, 10)} ${iso.slice(11, 19)}`;
    },

    /**
     * ISO 시각 → "YYYY-MM-DD" (locale 기반 TZ offset 적용)
     */
    formatDateOnly(v) {
      if (!v) return '-';
      const d = new Date(v);
      if (isNaN(d.getTime())) return '-';
      const offsetMs = this.timezoneInfo.offsetHours * 60 * 60 * 1000;
      const local = new Date(d.getTime() + offsetMs);
      return local.toISOString().slice(0, 10);
    },

    /**
     * 숫자 → locale 천 단위 구분 (예: "1,234,567")
     */
    formatNumber(v) {
      if (v === null || v === undefined || v === '') return '-';
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      const locale = this.$i18n?.locale || 'zh-CN';
      return n.toLocaleString(locale);
    },

    /**
     * 밀리초 → 사람 친화 표시 ("500ms" / "12.3s" / "1m 30s")
     */
    formatDuration(ms) {
      if (ms === null || ms === undefined || ms === '') return '-';
      const n = Number(ms);
      if (!Number.isFinite(n)) return '-';
      if (n < 1000) return `${n}ms`;
      if (n < 60_000) return `${(n / 1000).toFixed(1)}s`;
      const min = Math.floor(n / 60_000);
      const sec = ((n % 60_000) / 1000).toFixed(0);
      return `${min}m ${sec}s`;
    },

    /**
     * 문자열 truncate (max 길이 초과 시 "…" 추가)
     */
    truncate(s, max) {
      if (!s) return '-';
      const str = String(s);
      return str.length > max ? str.slice(0, max) + '…' : str;
    },

    /**
     * dayjs (또는 호환 객체) → "YYYYMMDD" (URL/API용)
     */
    toYYYYMMDD(d) {
      if (!d || typeof d.year !== 'function') return undefined;
      const yyyy = d.year();
      const mm = String(d.month() + 1).padStart(2, '0');
      const dd = String(d.date()).padStart(2, '0');
      return `${yyyy}${mm}${dd}`;
    },

    // ── pagination handlers (this.pagination + this.pageSize + this.fetchData 가정) ──

    onSortChange() {
      if (this.pagination) this.pagination.current = 1;
      this.fetchData?.();
    },

    onPageChange(page) {
      if (this.pagination) this.pagination.current = page;
      this.fetchData?.();
    },

    onPageSizeChange(current, size) {
      if (typeof size !== 'number' || !Number.isFinite(size) || size <= 0) return;
      this.pageSize = size;
      if (this.pagination) this.pagination.current = 1;
      this.fetchData?.();
    },

    onToolbarPageSizeChange(value) {
      this.onPageSizeChange(1, value);
    },

    /**
     * a-pagination show-total용 함수 — "총 {N}건" 라벨.
     * 사용: :show-total="totalLabel"
     */
    totalLabel(total) {
      if (this.$te('dealerGsp_total_count')) {
        return this.$t('dealerGsp_total_count', { count: total.toLocaleString(this.$i18n?.locale || 'zh-CN') });
      }
      return `${total.toLocaleString(this.$i18n?.locale || 'zh-CN')}`;
    },

    /**
     * URL ?key1=&key2= → filters에 채우기 (새로고침/딥링크/뒤로가기 유지).
     * 자식 컴포넌트에서 override 권장: 어떤 query 키를 받을지 명시.
     * 기본: deserializeQueryToFilters를 통해 타입 인식 복원 수행.
     */
    applyQueryToFilters() {
      if (!this.filters) return;
      this.deserializeQueryToFilters(this.$route?.query || {}, this.filters);
    },

    // ────────────────────────────────────────────────────────
    // SPEC-CHINA-ECOUNT-DEALER-FILTER-URL-SYNC-001
    // 양방향 URL query ↔ filters 동기화 유틸리티
    // ────────────────────────────────────────────────────────

    /**
     * filters 객체 → URL query 파라미터 객체 변환.
     *
     * 변환 규칙:
     * - 빈 문자열 / null / undefined / 빈 배열 / false → 제외 (URL 깨끗하게 유지)
     * - 배열 → CSV (예: customerCodes=A,B,C)
     * - boolean true → '1'
     * - dayjs 단일 객체 → 'YYYY-MM-DD'
     * - dayjs 범위 배열 [from, to] → '<key>From', '<key>To' 두 키로 분해
     * - 숫자 → String(n)
     * - 그 외 → String(v)
     *
     * @param {Object} filters — 현재 필터 객체
     * @returns {Object} — $router.replace({ query: ... })에 전달할 query 객체
     */
    serializeFiltersToQuery(filters) {
      if (!filters || typeof filters !== 'object') return {};
      const query = {};
      for (const [key, val] of Object.entries(filters)) {
        // null / undefined 제외
        if (val === null || val === undefined) continue;

        // boolean: false 제외, true → '1'
        if (typeof val === 'boolean') {
          if (val) query[key] = '1';
          continue;
        }

        // 배열 처리
        if (Array.isArray(val)) {
          // 빈 배열 제외
          if (val.length === 0) continue;
          // dayjs 범위 배열 [from, to] → <key>From / <key>To
          if (val.length === 2 && val[0] && typeof val[0] === 'object' && typeof val[0].format === 'function') {
            const fromKey = key + 'From';
            const toKey = key + 'To';
            if (val[0]) query[fromKey] = val[0].format('YYYY-MM-DD');
            if (val[1]) query[toKey] = val[1].format('YYYY-MM-DD');
          } else {
            // 일반 배열 → CSV
            query[key] = val.join(',');
          }
          continue;
        }

        // dayjs 단일 객체
        if (typeof val === 'object' && typeof val.format === 'function') {
          query[key] = val.format('YYYY-MM-DD');
          continue;
        }

        // 숫자
        if (typeof val === 'number') {
          query[key] = String(val);
          continue;
        }

        // 문자열: 빈 문자열 제외
        if (typeof val === 'string') {
          if (val !== '') query[key] = val;
          continue;
        }

        // 그 외 객체 등: 무시
      }
      return query;
    },

    /**
     * URL query → filters 객체 복원.
     * 현재 filters 값의 타입을 보고 자동 추론하여 적절히 변환.
     *
     * 변환 규칙 (filters[key] 초기값 타입 기준):
     * - 배열 → query[key]를 CSV 분해하여 배열로 복원
     * - boolean → '1'/'true' → true, 그 외 → false
     * - dayjs 단일 or null이고 <key>가 직접 있으면 dayjs로 복원
     * - <key>From + <key>To 쌍이 있으면 [dayjs, dayjs] 범위 배열로 복원
     * - 그 외 → String으로 적용
     *
     * query에 없는 키는 기존 filters 값 유지 (덮어쓰지 않음).
     *
     * @param {Object} query — $route.query 객체
     * @param {Object} filters — 현재 filters 객체 (in-place 변경)
     */
    deserializeQueryToFilters(query, filters) {
      if (!query || !filters) return;

      for (const key of Object.keys(filters)) {
        const currentVal = filters[key];

        // 배열 타입 → CSV 복원
        if (Array.isArray(currentVal)) {
          // dayjs 범위 배열 여부 확인: <key>From + <key>To 쌍
          const fromKey = key + 'From';
          const toKey = key + 'To';
          if (query[fromKey] !== undefined || query[toKey] !== undefined) {
            const from = query[fromKey] ? dayjs(query[fromKey]) : null;
            const to = query[toKey] ? dayjs(query[toKey]) : null;
            if (from || to) {
              filters[key] = [from, to];
            }
            continue;
          }
          // 일반 배열 → CSV 분해
          if (query[key] !== undefined && query[key] !== '') {
            filters[key] = String(query[key]).split(',').filter(Boolean);
          }
          continue;
        }

        // boolean 타입
        if (typeof currentVal === 'boolean') {
          if (query[key] !== undefined) {
            filters[key] = query[key] === '1' || query[key] === 'true';
          }
          continue;
        }

        // dayjs 단일 또는 null (snapshotDate 같은 단일 날짜 피커)
        if (currentVal === null || (currentVal && typeof currentVal === 'object' && typeof currentVal.format === 'function')) {
          // <key>From/<key>To 쌍이 있으면 범위 배열로 복원 (이 케이스는 배열 타입에서 처리)
          if (query[key] !== undefined && query[key] !== '') {
            const parsed = dayjs(String(query[key]));
            if (parsed.isValid()) filters[key] = parsed;
          }
          continue;
        }

        // 문자열 / 숫자 → String 적용
        if (query[key] !== undefined && query[key] !== null && query[key] !== '') {
          filters[key] = String(query[key]);
        }
      }
    },

    /**
     * filters ↔ URL query 양방향 동기화 설정.
     * mounted() 내에서 applyQueryToFilters() 호출 직후 1회 호출.
     *
     * 동작:
     * 1. filters를 deep watch하여 변경 시 300ms debounce 후 $router.replace 실행
     * 2. 직렬화 결과가 현재 query와 동일하면 router.replace skip (무한 루프 방지)
     * 3. replace로 발생한 $route.query 변경은 직전 serialize 결과와 비교 후
     *    동일하면 fetchData 재호출 없이 skip (기존 '$route.query' watch와 연동)
     * 4. beforeUnmount에서 watch 핸들러와 timer를 자동 정리
     *
     * 주의: filters watch는 mixin이 아닌 이 메서드에서 수동으로 등록.
     *       각 화면의 기존 filters watch(debounce refresh)와 중복되지 않음.
     */
    /**
     * 필터 동기화 replace가 덮어써서는 안 되는 query 키 목록.
     * 기본값은 빈 배열 — 기존 소비 화면의 동작은 그대로다.
     * 필터와 무관한 화면 모드를 query로 들고 있는 화면(예: ManualWorkOrders의
     * missingRandomNo)만 override하여 자기 키를 보존한다.
     * @returns {string[]}
     */
    preservedQueryKeys() {
      return [];
    },

    /**
     * serialize된 필터 query에 preservedQueryKeys()에 해당하는 현재 query 값을 되붙인다.
     * 보존 키가 없으면 입력을 그대로 돌려주므로 기존 화면엔 영향이 없다.
     */
    mergePreservedQuery(newQuery) {
      const keys = this.preservedQueryKeys?.() || [];
      if (!keys.length) return newQuery;
      const current = this.$route?.query || {};
      const merged = { ...newQuery };
      keys.forEach((k) => {
        if (current[k] !== undefined && current[k] !== null && current[k] !== '') {
          merged[k] = current[k];
        }
      });
      return merged;
    },

    setupQueryFilterSync() {
      if (!this.$watch) return;
      // 마지막으로 serialize한 query 문자열 (중복 replace 방지)
      this._lastSerializedQueryStr = JSON.stringify(
        this.mergePreservedQuery(this.serializeFiltersToQuery(this.filters)),
      );

      this._queryFilterSyncUnwatch = this.$watch(
        'filters',
        () => {
          // 기존 debounce timer 취소 후 재등록
          if (this._queryFilterSyncTimer) {
            clearTimeout(this._queryFilterSyncTimer);
          }
          this._queryFilterSyncTimer = setTimeout(() => {
            this._queryFilterSyncTimer = null;
            const newQuery = this.serializeFiltersToQuery(this.filters);
            // 보존 키(preservedQueryKeys)를 되붙인 최종 query로 비교·replace한다.
            // 비교 대상을 merge 이전 값으로 두면 보존 키 때문에 매 tick replace가 발생한다.
            const mergedQuery = this.mergePreservedQuery(newQuery);
            const newQueryStr = JSON.stringify(mergedQuery);
            // 현재 $route.query와 동일하면 skip
            const currentQueryStr = JSON.stringify(this.$route?.query || {});
            if (newQueryStr === currentQueryStr) return;
            // 이전 serialize 결과와 동일해도 skip (filters가 원래대로 돌아온 경우)
            if (newQueryStr === this._lastSerializedQueryStr) return;
            this._lastSerializedQueryStr = newQueryStr;
            this.$router?.replace({ query: mergedQuery }).catch(() => {});
          }, 300);
        },
        { deep: true },
      );
    },
  },

  watch: {
    // URL query 변경 시 자동 재조회 (뒤로가기/Forward + 드릴다운 deeplink)
    // setupQueryFilterSync의 replace 결과로 이 watch가 트리거되면 직전 serialize와
    // 비교하여 동일한 경우 fetchData 재호출을 skip함 (화면이 override할 수 있도록 기본 구현 제공)
    '$route.query'(newQuery) {
      const newQueryStr = JSON.stringify(newQuery);
      if (newQueryStr === this._lastSerializedQueryStr) {
        // setupQueryFilterSync가 발행한 replace에 의한 변경 — fetchData 재호출 불필요
        return;
      }
      this.applyQueryToFilters?.();
      this.fetchData?.();
    },
  },
};
